import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography, useMediaQuery } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import StarIcon from '@mui/icons-material/Star';
import { PsfColors } from '../../theme';
import { ProjectSummary } from '../../services/api';
import { PowerMeter } from '../QuestKit';
import { CityScene } from './cityScene';

// ── Scroll journey for Benchmark vs New Launch ─────────────────────────────
// Six full-height question sections, then the results. The page scrolls
// natively (proximity snap); scroll position drives the 3D city behind it
// and a depth reveal on each section. Answers apply live, so the match count
// and the glowing towers narrow as the user goes.

export interface JourneyApi {
  next: () => void;
  goTo: (index: number) => void;
}

export interface JourneyStep {
  key: string;
  label: string;
  question: string;
  /** Word or phrase in `question` painted in the step accent. */
  highlight?: string;
  hint: string;
  icon: React.ReactElement;
  accent: string;
  answered: boolean;
  badge?: string;
  render: (api: JourneyApi) => React.ReactNode;
}

interface BenchmarkJourneyProps {
  c: PsfColors;
  steps: JourneyStep[];
  renderResults: (api: JourneyApi) => React.ReactNode;
  projects: ProjectSummary[];
  matchedNames: Set<string>;
  matchCount: number;
  loading: boolean;
  criteriaCount: number;
  onEnterResults: () => void;
  onReset: () => void;
  /** Extra controls in the top bar, e.g. the Classic | Guided switch. */
  headerActions?: React.ReactNode;
}

const MONO = 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace';
const pad2 = (n: number) => String(n).padStart(2, '0');

const flashIn = keyframes`
  0% { opacity: 0; }
  12% { opacity: 1; }
  100% { opacity: 0; }
`;
const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(6px); }
`;

export default function BenchmarkJourney({
  c, steps, renderResults, projects, matchedNames, matchCount, loading, criteriaCount, onEnterResults, onReset, headerActions,
}: BenchmarkJourneyProps) {
  const theme = useTheme();
  const mode = theme.palette.mode === 'light' ? 'light' : 'dark';
  const compact = useMediaQuery('(max-width:899px)');
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const sceneBg = mode === 'dark' ? '#041026' : '#e8eef6';
  const resultsIndex = steps.length;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Array<HTMLElement | null>>([]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneRef = useRef<CityScene | null>(null);
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const veilRef = useRef<HTMLDivElement | null>(null);
  const sideVeilRefs = useRef<Array<HTMLDivElement | null>>([null, null]);
  const flashRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef(0);
  const [active, setActive] = useState(0);
  const [sceneGen, setSceneGen] = useState(0);
  const [sceneFailed, setSceneFailed] = useState(false);
  const [slate, setSlate] = useState<{ index: number; on: boolean }>({ index: 0, on: false });
  const onEnterResultsRef = useRef(onEnterResults);
  onEnterResultsRef.current = onEnterResults;

  // ── 3D backdrop lifecycle ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let scene: CityScene;
    try {
      scene = new CityScene(canvas, { mode, reducedMotion, compact });
    } catch {
      setSceneFailed(true);
      return undefined;
    }
    sceneRef.current = scene;
    const host = canvas.parentElement!;
    const resize = () => scene.resize(host.clientWidth, host.clientHeight);
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    setSceneGen((gen) => gen + 1);
    return () => {
      observer.disconnect();
      scene.dispose();
      sceneRef.current = null;
    };
  }, [mode, reducedMotion, compact]);

  useEffect(() => {
    sceneRef.current?.setProjects(projects.map((project) => ({
      projectName: project.projectName,
      district: project.district,
      avgPsf: project.avgPsf,
      txnCount: project.txnCount,
    })));
  }, [projects, sceneGen]);

  useEffect(() => {
    sceneRef.current?.setMatches(matchedNames);
  }, [matchedNames, sceneGen]);

  // ── Scroll → progress, depth reveal, active section ──
  const measure = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const height = scroller.clientHeight || 1;
    const view = scroller.scrollTop + height / 2;
    const sections = sectionRefs.current.filter((el): el is HTMLElement => Boolean(el));
    if (!sections.length) return;
    const center = (el: HTMLElement) => el.offsetTop + el.offsetHeight / 2;

    let progress = sections.length - 1;
    for (let i = 0; i < sections.length - 1; i += 1) {
      const a = center(sections[i]);
      const b = center(sections[i + 1]);
      if (view < b) { progress = i + Math.max(0, Math.min(1, (view - a) / (b - a))); break; }
    }
    sceneRef.current?.setProgress(progress);

    sections.forEach((section, index) => {
      const d = (center(section) - view) / height;
      const ad = Math.min(1.4, Math.abs(d));
      const inner = section.querySelector<HTMLElement>('[data-journey-inner]');
      const numeral = section.querySelector<HTMLElement>('[data-journey-numeral]');
      const isResults = index === sections.length - 1;
      if (inner) {
        if (ad < 0.004) {
          inner.style.transform = 'none';
          inner.style.filter = 'none';
          inner.style.opacity = '1';
        } else if (reducedMotion) {
          inner.style.transform = 'none';
          inner.style.filter = 'none';
          inner.style.opacity = String(Math.max(0, 1 - ad * 0.9));
        } else {
          const lift = d * (isResults ? 50 : 90);
          const scale = isResults ? 1 : 1 - ad * 0.06;
          inner.style.transform = `translate3d(0, ${lift.toFixed(1)}px, 0) scale(${scale.toFixed(4)})`;
          inner.style.filter = isResults || ad < 0.02 ? 'none' : `blur(${(ad * 7).toFixed(2)}px)`;
          inner.style.opacity = String(Math.max(0, 1 - ad * 1.15));
        }
      }
      if (numeral) {
        numeral.style.transform = reducedMotion ? 'translateY(-50%)' : `translateY(calc(-50% + ${(d * -220).toFixed(1)}px))`;
        numeral.style.opacity = String(Math.max(0, 1 - ad * 1.3));
      }
    });

    if (progressBarRef.current) {
      progressBarRef.current.style.transform = `scaleX(${(progress / Math.max(1, sections.length - 1)).toFixed(4)})`;
    }
    // The veil thickens behind the results table so it stays readable.
    if (veilRef.current) {
      veilRef.current.style.opacity = String(Math.max(0, Math.min(1, progress - (sections.length - 2))));
    }

    // Questions alternate sides: the readability veil follows the copy.
    const index = Math.round(progress);
    const side = index >= sections.length - 1 ? -1 : index % 2;
    sideVeilRefs.current.forEach((veil, n) => { if (veil) veil.style.opacity = n === side ? '1' : '0'; });
    if (index !== activeRef.current) {
      activeRef.current = index;
      setActive(index);
    }
  }, [reducedMotion]);

  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return undefined;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; measure(); });
    };
    measure();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    const observer = new ResizeObserver(onScroll);
    observer.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [measure]);

  // ── Scene cut on every section change ──
  const firstCut = useRef(true);
  useEffect(() => {
    if (firstCut.current) { firstCut.current = false; return undefined; }
    sceneRef.current?.cut();
    if (active === resultsIndex) onEnterResultsRef.current();
    setSlate({ index: active, on: true });
    const flash = flashRef.current;
    if (flash && !reducedMotion) {
      flash.classList.remove('on');
      void flash.offsetWidth;
      flash.classList.add('on');
    }
    const timer = window.setTimeout(() => setSlate((s) => ({ ...s, on: false })), 2200);
    return () => window.clearTimeout(timer);
  }, [active, resultsIndex, reducedMotion]);

  const goTo = useCallback((index: number) => {
    const scroller = scrollRef.current;
    const section = sectionRefs.current[index];
    if (!scroller || !section) return;
    scroller.scrollTo({ top: section.offsetTop, behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [reducedMotion]);

  const apiFor = (index: number): JourneyApi => ({ next: () => goTo(index + 1), goTo });
  const onResults = active >= resultsIndex;
  const labels = [...steps.map((step) => step.label), 'Results'];
  const accents = [...steps.map((step) => step.accent), c.yellow];

  const glass = {
    bgcolor: alpha(c.surfaceCard, mode === 'dark' ? 0.78 : 0.86),
    border: `1px solid ${alpha(mode === 'dark' ? '#5cc8ff' : c.navy, 0.18)}`,
    backdropFilter: compact ? 'none' : 'blur(14px)',
    WebkitBackdropFilter: compact ? 'none' : 'blur(14px)',
  } as const;

  const renderQuestion = (step: JourneyStep) => {
    if (!step.highlight || !step.question.includes(step.highlight)) return step.question;
    const [before, after] = step.question.split(step.highlight);
    return <>{before}<Box component="span" sx={{ color: step.accent }}>{step.highlight}</Box>{after}</>;
  };

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, height: '100%', overflow: 'hidden', bgcolor: sceneBg }}>
      {/* ── backdrop ── */}
      <Box aria-hidden sx={{
        position: 'absolute', inset: 0, zIndex: 0,
        background: mode === 'dark'
          ? 'radial-gradient(1200px 700px at 78% 22%, #0a2c5c, transparent), linear-gradient(#020f24, #041e42)'
          : 'radial-gradient(1200px 700px at 78% 22%, #ffffff, transparent), linear-gradient(#eef3f9, #dfe7f1)',
      }}>
        {!sceneFailed && (
          <canvas
            key={`${mode}-${compact}-${reducedMotion}`}
            ref={canvasRef}
            style={{ width: '100%', height: '100%', display: 'block' }}
          />
        )}
      </Box>
      {[90, 270].map((angle, n) => (
        <Box
          key={angle}
          ref={(el: HTMLDivElement | null) => { sideVeilRefs.current[n] = el; }}
          aria-hidden
          sx={{
            position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', opacity: n === 0 ? 1 : 0,
            transition: 'opacity 700ms ease',
            background: compact
              ? `linear-gradient(180deg, ${alpha(sceneBg, 0.5)} 0%, ${alpha(sceneBg, 0.68)} 100%)`
              : `linear-gradient(${angle}deg, ${alpha(sceneBg, 0.88)} 0%, ${alpha(sceneBg, 0.5)} 44%, ${alpha(sceneBg, 0.02)} 100%)`,
          }}
        />
      ))}
      <Box ref={veilRef} aria-hidden sx={{
        position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', opacity: 0,
        background: `radial-gradient(120% 90% at 50% 100%, ${alpha(sceneBg, 0.25)}, ${alpha(sceneBg, 0.7)})`,
      }} />
      <Box ref={flashRef} aria-hidden sx={{
        position: 'absolute', inset: 0, zIndex: 4, pointerEvents: 'none', opacity: 0,
        background: `radial-gradient(60% 50% at 50% 50%, ${alpha(c.orangeLight, 0.22)}, transparent 70%)`,
        mixBlendMode: mode === 'dark' ? 'screen' : 'multiply',
        '&.on': { animation: `${flashIn} 900ms ease-out` },
      }} />

      {/* ── progress line ── */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 5, bgcolor: alpha(c.border, 0.4) }}>
        <Box ref={progressBarRef} sx={{
          height: '100%', transformOrigin: 'left', transform: 'scaleX(0)',
          background: `linear-gradient(90deg, ${c.navyLight}, ${c.orange}, ${c.yellow})`,
        }} />
      </Box>

      {/* ── HUD ── */}
      <Box sx={{
        position: 'absolute', top: 10, left: { xs: 12, md: 24 }, right: { xs: 12, md: 24 }, zIndex: 5,
        display: 'flex', alignItems: 'center', gap: 1, pointerEvents: 'none',
        opacity: onResults ? 0 : 1, transform: onResults ? 'translateY(-12px)' : 'none',
        transition: 'opacity 300ms, transform 300ms',
        '& > *': { pointerEvents: onResults ? 'none' : 'auto' },
      }}>
        <Box sx={{ ...glass, display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.6, borderRadius: 99 }}>
          {loading
            ? <CircularProgress size={13} sx={{ color: c.orange }} />
            : <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c.orange, boxShadow: `0 0 10px ${c.orange}` }} />}
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: c.text, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
            {matchCount.toLocaleString()}
            <Box component="span" sx={{ color: c.textMuted, fontWeight: 600 }}> projects in play</Box>
          </Typography>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, pl: 1, borderLeft: `1px solid ${c.border}` }}>
            <PowerMeter count={criteriaCount} c={c} />
          </Box>
        </Box>
        <Box sx={{ flex: 1 }} />
        {headerActions && (
          <Box sx={{ ...glass, borderRadius: 2, display: 'flex', overflow: 'hidden' }}>{headerActions}</Box>
        )}
        <Button
          size="small"
          startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}
          onClick={onReset}
          sx={{ ...glass, color: c.textMuted, textTransform: 'none', fontSize: '0.72rem', borderRadius: 99, px: 1.4 }}
        >
          Start Over
        </Button>
      </Box>

      {/* ── slate: names the scene on every cut ── */}
      <Box aria-hidden sx={{
        position: 'absolute', zIndex: 5, pointerEvents: 'none', left: '50%', top: { xs: 54, md: 64 },
        transform: `translateX(-50%) translateY(${slate.on ? 0 : -8}px)`,
        opacity: slate.on ? 1 : 0, transition: 'opacity 400ms, transform 400ms', textAlign: 'center',
      }}>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.24em', color: accents[slate.index] ?? c.orange }}>
          {slate.index >= resultsIndex ? 'THE REVEAL' : `QUEST ${pad2(slate.index + 1)} / ${pad2(steps.length)}`}
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '0.95rem', md: '1.1rem' }, letterSpacing: '0.3em', color: c.text, textTransform: 'uppercase' }}>
          {labels[slate.index]}
        </Typography>
      </Box>

      {/* ── rail ── */}
      <Box
        component="nav"
        aria-label="Journey steps"
        sx={{
          position: 'absolute', right: 18, top: '50%', transform: 'translateY(-50%)', zIndex: 5,
          display: { xs: 'none', md: onResults ? 'none' : 'flex' }, flexDirection: 'column', gap: 1.1, alignItems: 'flex-end',
        }}
      >
        {labels.map((label, index) => {
          const current = index === active;
          const done = index < resultsIndex && steps[index].answered;
          const accent = accents[index];
          return (
            <Box
              key={label}
              component="button"
              type="button"
              onClick={() => goTo(index)}
              aria-current={current ? 'step' : undefined}
              sx={{
                all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                '&:hover .rail-label, &:focus-visible .rail-label': { opacity: 1, transform: 'none' },
                '&:focus-visible .rail-dot': { outline: `2px solid ${accent}`, outlineOffset: 3 },
              }}
            >
              <Typography className="rail-label" sx={{
                fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.14em', textTransform: 'uppercase',
                color: current ? accent : c.textMuted, opacity: current ? 1 : 0,
                transform: current ? 'none' : 'translateX(6px)', transition: 'opacity 200ms, transform 200ms',
              }}>
                {label}
              </Typography>
              <Box className="rail-dot" sx={{
                width: current ? 12 : 9, height: current ? 12 : 9, borderRadius: '50%',
                display: 'grid', placeItems: 'center',
                bgcolor: done || current ? accent : 'transparent',
                border: `1.5px solid ${done || current ? accent : c.textMuted}`,
                boxShadow: current ? `0 0 0 4px ${alpha(accent, 0.22)}, 0 0 14px ${accent}` : 'none',
                transition: 'all 200ms',
              }}>
                {done && !current && <CheckIcon sx={{ fontSize: 7, color: '#fff' }} />}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── the journey ── */}
      <Box
        ref={scrollRef}
        sx={{
          position: 'absolute', inset: 0, zIndex: 2, overflowY: 'auto', overflowX: 'hidden',
          scrollSnapType: 'y proximity', WebkitOverflowScrolling: 'touch',
        }}
      >
        {steps.map((step, index) => (
          <Box
            key={step.key}
            component="section"
            ref={(el: HTMLElement | null) => { sectionRefs.current[index] = el; }}
            aria-labelledby={`journey-q-${step.key}`}
            sx={{
              position: 'relative', minHeight: '100%', scrollSnapAlign: 'start',
              display: 'flex', alignItems: 'center',
              justifyContent: { xs: 'flex-start', md: index % 2 ? 'flex-end' : 'flex-start' },
              pl: { xs: 2, md: 'clamp(24px, 6vw, 96px)' },
              pr: { xs: 2, md: index % 2 ? 'clamp(64px, 7vw, 120px)' : 'clamp(24px, 6vw, 96px)' },
              pt: { xs: 9, md: 10 }, pb: { xs: 5, md: 6 },
            }}
          >
            <Typography data-journey-numeral aria-hidden sx={{
              position: 'absolute', top: '50%', transform: 'translateY(-50%)',
              ...(index % 2 ? { left: { md: '9vw' } } : { right: { md: '9vw' } }),
              display: { xs: 'none', md: 'block' }, pointerEvents: 'none', userSelect: 'none',
              fontWeight: 900, fontSize: 'clamp(9rem, 22vw, 20rem)', lineHeight: 1, letterSpacing: '-0.06em',
              color: 'transparent', WebkitTextStroke: `1.5px ${alpha(step.accent, 0.38)}`,
              willChange: 'transform, opacity',
            }}>
              {pad2(index + 1)}
            </Typography>

            <Box data-journey-inner sx={{ position: 'relative', width: '100%', maxWidth: 700, willChange: 'transform, opacity, filter' }}>
              {index === 0 && (
                <Typography sx={{ color: c.textMuted, fontSize: '0.8rem', fontWeight: 700, mb: 1.5 }}>
                  Benchmark vs New Launch · {steps.length} questions to your Personalized PSF
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                <Box sx={{ width: 26, height: '1px', bgcolor: step.accent }} />
                <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.18em', color: step.accent, textTransform: 'uppercase' }}>
                  Quest {pad2(index + 1)} / {pad2(steps.length)} · {step.label}
                </Typography>
                {step.badge && (
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, height: 18, borderRadius: 99,
                    bgcolor: c.yellow, color: c.navyDark, fontSize: '0.56rem', fontWeight: 900, letterSpacing: 0.4,
                  }}>
                    <StarIcon sx={{ fontSize: 11 }} />{step.badge}
                  </Box>
                )}
              </Box>
              <Typography
                id={`journey-q-${step.key}`}
                component="h2"
                sx={{
                  fontWeight: 800, color: c.text, lineHeight: 1.06, letterSpacing: '-0.02em',
                  fontSize: { xs: '1.75rem', md: 'clamp(2rem, 3.4vw, 2.9rem)' }, mb: 1,
                }}
              >
                {renderQuestion(step)}
              </Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '0.86rem', md: '0.95rem' }, mb: 2.5, maxWidth: 560 }}>
                {step.hint}
              </Typography>

              <Box sx={{ ...glass, borderRadius: 3, p: { xs: 1.5, md: 2 }, boxShadow: `0 24px 60px -30px ${alpha('#000', 0.6)}` }}>
                {step.render(apiFor(index))}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                {index > 0 && (
                  <Button
                    size="small"
                    startIcon={<ArrowUpwardIcon sx={{ fontSize: 16 }} />}
                    onClick={() => goTo(index - 1)}
                    sx={{ color: c.textMuted, textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Back
                  </Button>
                )}
                <Box sx={{ flex: 1 }} />
                {index === 0 && (
                  <Typography sx={{ color: c.textMuted, fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ArrowDownwardIcon sx={{ fontSize: 14, animation: reducedMotion ? 'none' : `${bob} 1.6s ease-in-out infinite` }} />
                    Scroll to continue
                  </Typography>
                )}
                {index < steps.length - 1 ? (
                  <Button
                    variant={step.answered ? 'contained' : 'outlined'}
                    endIcon={<ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                    onClick={() => goTo(index + 1)}
                    sx={{
                      textTransform: 'none', fontWeight: 800, borderRadius: 99, px: 2.25,
                      ...(step.answered
                        ? { bgcolor: step.accent, color: '#fff', '&:hover': { bgcolor: step.accent, filter: 'brightness(1.08)' } }
                        : { color: c.text, borderColor: c.border, '&:hover': { borderColor: step.accent } }),
                    }}
                  >
                    {step.answered ? 'Next' : 'Skip'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<RocketLaunchIcon sx={{ fontSize: 17 }} />}
                    onClick={() => goTo(resultsIndex)}
                    sx={{
                      textTransform: 'none', fontWeight: 800, borderRadius: 99, px: 2.5,
                      background: `linear-gradient(135deg, ${c.orange}, ${c.orangeLight})`,
                      boxShadow: `0 8px 26px -8px ${c.orange}`,
                      '&:hover': { background: `linear-gradient(135deg, ${c.orange}, ${c.orange})` },
                    }}
                  >
                    Reveal my Personalized PSF
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        ))}

        <Box
          component="section"
          ref={(el: HTMLElement | null) => { sectionRefs.current[resultsIndex] = el; }}
          aria-label="Results"
          sx={{ position: 'relative', height: '100%', scrollSnapAlign: 'start', display: 'flex', p: { xs: 0.5, md: 1.25 } }}
        >
          <Box data-journey-inner sx={{
            flex: 1, minWidth: 0, minHeight: 0, display: 'flex', borderRadius: { xs: 1.5, md: 2.5 }, overflow: 'hidden',
            bgcolor: alpha(c.surface, mode === 'dark' ? 0.92 : 0.95),
            border: `1px solid ${alpha(mode === 'dark' ? '#5cc8ff' : c.navy, 0.16)}`,
            boxShadow: `0 30px 80px -40px ${alpha('#000', 0.7)}`,
            willChange: 'transform, opacity',
          }}>
            {renderResults(apiFor(resultsIndex))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/** Large two-up answer tile for single-choice questions. */
export function ChoiceTile({
  icon, title, subtitle, accent, selected, onClick, c,
}: {
  icon: React.ReactElement;
  title: string;
  subtitle: string;
  accent: string;
  selected: boolean;
  onClick: () => void;
  c: PsfColors;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        all: 'unset', boxSizing: 'border-box', cursor: 'pointer', position: 'relative', overflow: 'hidden',
        display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.75, borderRadius: 2.5, minWidth: 0,
        border: `1.5px solid ${selected ? accent : c.border}`,
        background: selected ? `linear-gradient(150deg, ${alpha(accent, 0.22)}, ${alpha(accent, 0.04)} 70%)` : 'transparent',
        boxShadow: selected ? `0 0 0 4px ${alpha(accent, 0.12)}, 0 14px 30px -18px ${accent}` : 'none',
        transition: 'all 180ms',
        '&:hover': { borderColor: accent, transform: 'translateY(-2px)' },
        '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{
        width: 46, height: 46, flexShrink: 0, borderRadius: 2, display: 'grid', placeItems: 'center',
        bgcolor: selected ? accent : alpha(accent, 0.14), color: selected ? '#fff' : accent,
        transition: 'all 180ms', '& svg': { fontSize: 24 },
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: c.text }}>{title}</Typography>
        <Typography sx={{ fontSize: '0.78rem', color: c.textMuted, lineHeight: 1.45, mt: 0.25 }}>{subtitle}</Typography>
      </Box>
      {selected && (
        <Box sx={{
          position: 'absolute', top: 10, right: 10, width: 20, height: 20, borderRadius: '50%',
          bgcolor: accent, display: 'grid', placeItems: 'center',
        }}>
          <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />
        </Box>
      )}
    </Box>
  );
}
